'use client';

import { useState } from 'react';
import type { FormLocale } from '@jlg-website/shared';
import { CONFIRM_LEAD_MS } from '../shared/constants';
import { SubmitError } from '../shared/submit-error';
import type { CompletionAction, ConfirmCallback } from '../shared/type';
import { useCallbackSubmit } from '../shared/use-callback-submit';
import { CallNowConfirmStep } from './confirm-step';
import { CallNowSuccessStep } from './success-step';

type CallNowProps = {
  phone: string;
  /** Zone the visitor reads their times in, resolved by the caller from the ZIP code. */
  leadTimeZone: string;
  officeTimeZone: string;
  locale?: FormLocale;
  /** Own submit path: the backend handles an immediate call back differently from a booked slot. */
  onConfirm?: ConfirmCallback;
  /** Returns to the question that opened the scheduler, so a time can be picked instead. */
  onBack: () => void;
  completionAction?: CompletionAction;
};

/**
 * The "yes, call me now" answer to the question the scheduler opens with: a single confirmation,
 * with no calendar, no slot and no confirmation deadline — only the notes and the confirmation.
 *
 * The booked instant is `CONFIRM_LEAD_MS` ahead of the press rather than the press itself. The
 * endpoint rejects an instant that is no longer in the future, and the same two minutes the picker
 * reserves to confirm a slot are what let this request survive a slow connection.
 */
export function CallNow({
  phone,
  leadTimeZone,
  officeTimeZone,
  locale = 'en',
  onConfirm,
  onBack,
  completionAction,
}: CallNowProps) {
  const [notes, setNotes] = useState('');
  const [bookedAt, setBookedAt] = useState<string | null>(null);

  const { submitting, failed, submit } = useCallbackSubmit(onConfirm);

  const handleConfirm = async () => {
    const callbackOn = new Date(Date.now() + CONFIRM_LEAD_MS).toISOString();
    const booked = await submit({ callback_on: callbackOn, tz: officeTimeZone, notes });

    if (booked) setBookedAt(booked.callback_on);
  };

  if (bookedAt) {
    return (
      <div className="w-full">
        <CallNowSuccessStep
          bookedAt={bookedAt}
          timeZone={leadTimeZone}
          phone={phone}
          locale={locale}
          completionAction={completionAction}
        />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-5">
      <CallNowConfirmStep
        phone={phone}
        notes={notes}
        locale={locale}
        submitting={submitting}
        onChangeNotes={setNotes}
        onBack={onBack}
        onConfirm={handleConfirm}
      />

      {failed && <SubmitError locale={locale} />}
    </div>
  );
}

export default CallNow;
